import {
  Checkbox,
  Grid,
  PasswordInput,
  Select,
  Space,
  Text,
  TextInput,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { cities, provinces } from '../../../../data/province-city.array';
import { Countries } from '../../../../enums/countries.enum';
import { BranchUserDetailsForm } from '../../../../type/entity.types';
import PhoneCountryCodeIcon from '../../../icons/phone-country-code-icon.component';
import XCol4 from '../../../x-components/grid-col/x-col-4.component';
import XCol6 from '../../../x-components/grid-col/x-col-6.component';

function BranchUserDetails(props: Props) {
  const { form } = props;

  return (
    <>
      <Text size='xl' weight={700}>
        User Details
      </Text>
      <Space h='xl' />
      <Grid>
        <XCol4>
          <TextInput
            label={labels.firstName}
            placeholder={placeholders.firstName}
            {...form.getInputProps('firstName')}
          />
        </XCol4>
        <XCol4>
          <TextInput
            label={labels.middleName}
            placeholder={placeholders.middleName}
            disabled={form.values.noMiddleName}
            {...form.getInputProps('middleName')}
          />
          <Space my='xs' />
          <Checkbox
            label="I don't have a middle name"
            checked={form.values.noMiddleName}
            onChange={() => {
              const noMiddleName = !form.values.noMiddleName;
              form.setFieldValue('noMiddleName', noMiddleName);
              form.setFieldValue('middleName', noMiddleName ? '.' : '');
            }}
          />
        </XCol4>
        <XCol4>
          <TextInput
            label={labels.lastName}
            placeholder={placeholders.lastName}
            {...form.getInputProps('lastName')}
          />
        </XCol4>

        <Grid.Col>
          <Checkbox
            label={labels.copyBranchAddress}
            {...form.getInputProps('copyBranchAddress', {
              type: 'checkbox',
            })}
          />
        </Grid.Col>

        {!form.values.copyBranchAddress && (
          <>
            <Grid.Col span={12}>
              <TextInput
                label={labels.bldgNameNo}
                placeholder={placeholders.bldgNameNo}
                {...form.getInputProps('buildingNameAndNumber')}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <TextInput
                label={labels.line1}
                placeholder={placeholders.line1}
                {...form.getInputProps('line1')}
              />
            </Grid.Col>
            <XCol6>
              <Select
                label={labels.province}
                placeholder={placeholders.province}
                data={provinces}
                value={form.values.state}
                onChange={(value: string) => {
                  form.setFieldValue('state', value);
                  form.setFieldValue('city', '');
                }}
                searchable
                nothingFound='No options'
                error={form.errors.state}
              />
            </XCol6>
            <XCol6>
              <Select
                label={labels.city}
                placeholder={placeholders.city}
                data={
                  form.values.state.length > 0 ? cities[form.values.state] : []
                }
                disabled={form.values.state === ''}
                searchable
                nothingFound='No options'
                value={form.values.city}
                onChange={(value: string) => {
                  form.setFieldValue('city', value);
                }}
                error={form.errors.city}
              />
            </XCol6>
            <XCol4>
              <TextInput
                label={labels.barangay}
                placeholder={placeholders.barangay}
                {...form.getInputProps('barangay')}
              />
            </XCol4>
            <XCol4>
              <TextInput
                label={labels.postalCode}
                placeholder={placeholders.postalCode}
                {...form.getInputProps('postalCode')}
              />
            </XCol4>
            <XCol4>
              <Select
                label={labels.country}
                placeholder={placeholders.country}
                data={[
                  {
                    label: 'Philippines',
                    value: Countries.PHILIPPINES.toString(),
                  },
                ]}
                value={form.values.countryId.toString()}
                onChange={(value: string) => {
                  form.setFieldValue('countryId', parseInt(value));
                }}
                error={form.errors.countryId}
              />
            </XCol4>
          </>
        )}

        <XCol4>
          <TextInput
            label={labels.primary}
            placeholder={placeholders.primary}
            icon={<PhoneCountryCodeIcon />}
            iconWidth={48}
            {...form.getInputProps('primary')}
          />
        </XCol4>
        <XCol4>
          <TextInput
            label={labels.fax}
            placeholder={placeholders.fax}
            {...form.getInputProps('fax')}
          />
        </XCol4>
        <XCol4>
          <TextInput
            label={labels.mobile}
            placeholder={placeholders.mobile}
            icon={<PhoneCountryCodeIcon />}
            iconWidth={48}
            {...form.getInputProps('mobile')}
          />
        </XCol4>
        <XCol6>
          <TextInput
            label={labels.userEmail}
            placeholder={placeholders.userEmail}
            {...form.getInputProps('userEmail')}
          />
        </XCol6>
        <XCol6>
          <TextInput
            label={labels.username}
            placeholder={placeholders.username}
            {...form.getInputProps('username')}
          />
        </XCol6>
        <XCol4>
          <PasswordInput
            label={labels.password}
            placeholder={placeholders.password}
            {...form.getInputProps('password')}
          />
        </XCol4>
        <XCol4>
          <PasswordInput
            label={labels.confirmPassword}
            placeholder={placeholders.confirmPassword}
            {...form.getInputProps('confirmPassword')}
          />
        </XCol4>
        <XCol4>
          <PasswordInput
            label={labels.pin}
            placeholder={placeholders.pin}
            {...form.getInputProps('pin')}
            maxLength={4}
          />
        </XCol4>
      </Grid>
    </>
  );
}

export default BranchUserDetails;

interface Props {
  form: UseFormReturnType<BranchUserDetailsForm>;
}

const labels = {
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  username: 'Username',
  password: 'Password',
  confirmPassword: 'Confirm password',
  userEmail: 'Email',
  bldgNameNo: 'Building name and number',
  line1: 'Street address',
  city: 'City',
  province: 'Province',
  postalCode: 'Postal code',
  country: 'Country',
  primary: 'Primary contact number',
  fax: 'FAX',
  barangay: 'Barangay',
  mobile: 'Mobile phone number',
  pin: 'PIN',
  copyBranchAddress: "Use Branch's address details",
};

const placeholders = {
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  userEmail: 'Email',
  username: 'Username',
  password: 'Password',
  confirmPassword: 'Confirm password',
  bldgNameNo: 'Building name and number',
  line1: 'Street address',
  city: 'City',
  province: 'Province',
  postalCode: 'Postal code',
  country: 'Country',
  primary: 'Primary contact number',
  fax: 'FAX',
  barangay: 'Barangay',
  mobile: 'Mobile phone number',
  pin: 'PIN',
};
